/**
 * "Importera leadslista" (leadspanelen): ladda upp .xlsx från säljpartner →
 * förhandsgranskning → importera. Logiken ligger i leadslistaImport.js
 * (plan) och leadslistaImportData.js (skrivning). Inget skrivs förrän
 * användaren klickar Importera, och bara när alla osäkra träffar och okända
 * värden är avgjorda.
 */
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Button, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Chip, Checkbox, Table, TableHead, TableBody, TableRow, TableCell, Tooltip, Link, CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { db } from '../../firebase/config';
import { tolkaArbetsbok, planeraImport, olostaOkanda } from './leadslistaImport';
import { utforImport } from './leadslistaImportData';
import { SegmentValjare, OrganisationstypValjare } from './KategoriVal';

const ATGARD = {
  ny_kund:    { text: 'Ny kund + Flatbox-lead', farg: '#1565C0', bg: '#E3F2FD' },
  nytt_lead:  { text: 'Nytt Flatbox-lead', farg: '#2E7D32', bg: '#E8F5E9' },
  oforandrad: { text: 'Har Flatbox-lead – oförändrat', farg: '#616161', bg: '#F5F5F5' },
  bekrafta:   { text: 'Bekräfta kund', farg: '#E65100', bg: '#FFF3E0' },
  hoppa_over: { text: 'Hoppas över', farg: '#9E9E9E', bg: '#FAFAFA' },
};
const pct = x => `${Math.round(x * 100)} %`;

export default function LeadslistaImportDialog({ open, onClose, companies, leads, products, departments, uid, onImported }) {
  const [partner, setPartner] = useState('Comdeva');
  const [lista, setLista] = useState(null);   // tolkaArbetsbok
  const [filnamn, setFilnamn] = useState('');
  const [val, setVal] = useState({});         // radnr → { segment, organisationstyp, kundId, hoppaOver }
  const [fel, setFel] = useState('');
  const [importerar, setImporterar] = useState(false);
  const [resultat, setResultat] = useState(null);

  const flatbox = products.find(p => /flatbox/i.test(p.name || ''));
  const mvl = departments.find(d => d.name?.toUpperCase().includes('MVL'));
  const flatboxIds = useMemo(() => new Set(products.filter(p => /flatbox/i.test(p.name || '')).map(p => p.id)), [products]);
  const plan = useMemo(() => (lista ? planeraImport(lista.rader, companies, leads, flatboxIds, val) : []),
    [lista, companies, leads, flatboxIds, val]);

  const obekraftade = plan.filter(p => p.atgard === 'bekrafta');
  const okanda = lista ? olostaOkanda(lista.rader.filter(r => !val[r.radnr]?.hoppaOver), val) : [];
  const antal = k => plan.filter(p => p.atgard === k).length;

  function aterstall() {
    setLista(null); setFilnamn(''); setVal({}); setFel(''); setResultat(null);
  }
  function stang() { aterstall(); onClose(); }

  async function lasFil(file) {
    setFel(''); setResultat(null); setVal({});
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const flikar = Object.fromEntries(wb.SheetNames.map(n => [n, XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, defval: '' })]));
      const t = tolkaArbetsbok(flikar);
      if (!t) { setFel('Hittar ingen flik med kolumnen "Organisation".'); return; }
      setLista(t); setFilnamn(file.name);
    } catch (e) {
      setFel(`Filen kunde inte läsas: ${e.message || e}`);
    }
  }

  const satt = (radnr, falt) => setVal(v => ({ ...v, [radnr]: { ...v[radnr], ...falt } }));

  async function importera() {
    setImporterar(true); setFel('');
    try {
      const res = await utforImport({
        db, plan, partner: partner.trim() || 'Comdeva', uid,
        flatboxProductId: flatbox.id, departmentIds: mvl ? [mvl.id] : [],
      });
      setResultat(res);
      onImported?.();
    } catch (e) {
      setFel(`Importen avbröts: ${e.message || e}`);
    } finally {
      setImporterar(false);
    }
  }

  const kundNamn = id => companies.find(c => c.id === id)?.name || '';

  return (
    <Dialog open={open} onClose={stang} maxWidth="xl" fullWidth>
      <DialogTitle>
        Importera leadslista
        {lista && !resultat && (
          <Typography variant="body2" color="text.secondary">
            {filnamn}, flik "{lista.flik}": {lista.rader.length} organisationer
            {lista.overhoppade.length ? `, ${lista.overhoppade.length} segmentrader överhoppade` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {fel && <Alert severity="error" sx={{ mb: 2 }}>{fel}</Alert>}
        {!flatbox && <Alert severity="error" sx={{ mb: 2 }}>Hittar ingen Flatbox-produkt – leads kan inte skapas.</Alert>}

        {!lista && !resultat && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" label="Partner" value={partner} onChange={e => setPartner(e.target.value)}
              helperText='Källan på leadet blir "<partner>: <Källa>"' />
            <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
              Välj .xlsx
              <input hidden type="file" accept=".xlsx,.xls"
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) lasFil(f); }} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              Fliken med kolumnen "Organisation" läses; rubrikraden hittas automatiskt.
            </Typography>
          </Box>
        )}

        {lista && !resultat && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {Object.entries(ATGARD).map(([k, a]) => antal(k) > 0 && (
                <Chip key={k} size="small" label={`${a.text}: ${antal(k)}`} sx={{ bgcolor: a.bg, color: a.farg, fontWeight: 600 }} />
              ))}
            </Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Ingen befintlig data skrivs över. Befintliga leads ändras inte; på befintliga kunder fylls bara tomma
              segment och organisationstyp i.
            </Alert>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Med', 'Organisation', 'Segment', 'Organisationstyp', 'Källa / kommentar', 'Matchad kund', 'Händer'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.map(p => {
                    const { rad, match } = p;
                    const v = val[rad.radnr] || {};
                    const a = ATGARD[p.atgard];
                    return (
                      <TableRow key={rad.radnr} sx={{ opacity: v.hoppaOver ? 0.5 : 1, verticalAlign: 'top' }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={!v.hoppaOver} onChange={e => satt(rad.radnr, { hoppaOver: !e.target.checked })} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography variant="body2" fontWeight={600}>{rad.organisation}</Typography>
                          <Typography variant="caption" color="text.disabled">rad {rad.radnr}</Typography>
                          {rad.kontaktperson && (
                            <Typography variant="caption" display="block">Kontakt: {rad.kontaktperson}{rad.roll ? `, ${rad.roll}` : ''}</Typography>
                          )}
                        </TableCell>
                        {['segment', 'organisationstyp'].map(f => {
                          const Valjare = f === 'segment' ? SegmentValjare : OrganisationstypValjare;
                          return (
                            <TableCell key={f} sx={{ minWidth: 200 }}>
                              <Valjare fullWidth label="" value={v[f] !== undefined ? v[f] : rad[f]}
                                onChange={x => satt(rad.radnr, { [f]: x })} disabled={v.hoppaOver} />
                              {rad.okant[f] && (
                                <Typography variant="caption" color={v[f] === undefined ? 'error' : 'text.secondary'} display="block">
                                  okänt värde: {rad.okant[f]}{v[f] === undefined ? ' – välj' : ''}
                                </Typography>
                              )}
                              {p.kund && p.kund[f] && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Kunden har: {p.kund[f]} (ändras inte)
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ minWidth: 160, maxWidth: 260 }}>
                          <Typography variant="body2">{rad.kalla || '–'}</Typography>
                          {(rad.kommentar || rad.status) && (
                            <Tooltip title={[rad.kommentar, rad.status && `Status: ${rad.status}`].filter(Boolean).join(' · ')}>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {[rad.kommentar, rad.status && `Status: ${rad.status}`].filter(Boolean).join(' · ')}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          {match.kandidater.length > 0 && (!match.saker || v.kundId !== undefined) ? (
                            <TextField select size="small" fullWidth disabled={v.hoppaOver}
                              value={v.kundId === undefined ? '' : (v.kundId || '__ny')}
                              onChange={e => satt(rad.radnr, { kundId: e.target.value === '__ny' ? null : e.target.value })}
                              error={p.atgard === 'bekrafta'} helperText={p.atgard === 'bekrafta' ? 'Bekräfta eller avvisa' : ''}>
                              <MenuItem value="" disabled>Välj…</MenuItem>
                              {match.kandidater.map(k => (
                                <MenuItem key={k.kund.id} value={k.kund.id}>{k.kund.name} ({pct(k.sakerhet)})</MenuItem>
                              ))}
                              <MenuItem value="__ny"><i>Ingen av dem – ny kund</i></MenuItem>
                            </TextField>
                          ) : p.kund ? (
                            <Typography variant="body2">
                              <Link component={RouterLink} to={`/crm/companies/${p.kund.id}`} target="_blank">{p.kund.name}</Link>
                              <Typography component="span" variant="caption" color="success.main"> · {pct(match.sakerhet)}</Typography>
                            </Typography>
                          ) : <Typography variant="body2" color="text.secondary">Ingen träff</Typography>}
                        </TableCell>
                        <TableCell sx={{ minWidth: 170 }}>
                          <Chip size="small" label={a.text} sx={{ bgcolor: a.bg, color: a.farg, fontWeight: 600 }} />
                          {p.flatboxLeads.length > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {p.flatboxLeads.map(l => l.title || l.id).join(', ')}
                            </Typography>
                          )}
                          {Object.keys(p.fyllI).length > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Fyller i: {Object.keys(p.fyllI).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {resultat && (
          <Box>
            <Alert severity={resultat.fel.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {resultat.nyaKunder} nya kunder, {resultat.nyaLeads} nya leads, {resultat.oforandrade} oförändrade,
              {' '}{resultat.overhoppade} överhoppade{resultat.ifyllda ? `, kategorier ifyllda på ${resultat.ifyllda} befintliga kunder` : ''}.
              {resultat.fel.length > 0 && ` ${resultat.fel.length} rader misslyckades.`}
            </Alert>
            {resultat.fel.map(f => (
              <Typography key={f.radnr} variant="body2" color="error">Rad {f.radnr} {f.organisation}: {f.fel}</Typography>
            ))}
            {resultat.rader.filter(r => r.leadId || r.atgard === 'oforandrad').map(r => (
              <Box key={r.radnr} sx={{ display: 'flex', gap: 1.5, py: 0.25, alignItems: 'baseline' }}>
                <Typography variant="body2" sx={{ minWidth: 260 }}>{r.organisation}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>{ATGARD[r.atgard]?.text}</Typography>
                {r.leadId && <Link component={RouterLink} to={`/crm/leads/${r.leadId}`} target="_blank" variant="body2">Lead</Link>}
                {r.companyId && <Link component={RouterLink} to={`/crm/companies/${r.companyId}`} target="_blank" variant="body2">Kund{r.atgard === 'ny_kund' ? ' (ny)' : `: ${kundNamn(r.companyId)}`}</Link>}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {lista && !resultat && (obekraftade.length > 0 || okanda.length > 0) && (
          <Typography variant="body2" color="warning.main" sx={{ mr: 'auto' }}>
            {[obekraftade.length && `${obekraftade.length} osäkra kundträffar att bekräfta eller avvisa`,
              okanda.length && `${okanda.length} rader med okända värden att välja om`].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {lista && !resultat && <Button onClick={aterstall}>Byt fil</Button>}
        <Button onClick={stang}>{resultat ? 'Stäng' : 'Avbryt'}</Button>
        {lista && !resultat && (
          <Button variant="contained" onClick={importera}
            disabled={importerar || !flatbox || obekraftade.length > 0 || okanda.length > 0}
            startIcon={importerar ? <CircularProgress size={14} /> : null}>
            Importera {plan.filter(p => ['ny_kund', 'nytt_lead', 'oforandrad'].includes(p.atgard)).length} rader
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
